import { UserProfile, UserWorkoutSession } from 'fitbeat.gym-core';
import { NextWorkout } from 'fitbeat.models';
import React from 'react';
import { translate } from 'react-i18next';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { connect } from 'react-redux';
import circlePlayIcon from '../../assets/images/circle-play-icon-orange.svg';
import studioWorkoutImage from '../../assets/images/header-images/studio-header.jpg';
import homeWorkoutImage from '../../assets/images/workout-summary-image.jpg';
import { hasAccessToAStudioGymSelector } from '../../auth/auth-selectors';
import { FitbeatLiteVideoIntro } from '../../auth/components/FitbeatLiteVideoIntro';
import { isBookingInOneHour } from '../../Bookings/utils';
import { Button, ButtonSize, ButtonTypes } from '../../components';
import { IAppStore } from '../../HomeWorkouts';
import WorkoutButtonWithCountdownAndActions from '../../Planner/components/WorkoutButtonWithCountdownAndActions';
import Theme from '../../theme';
import { TrackingImageTile } from '../../Tracking/components/TrackingImageTile';
import { closeWorkoutIntroVideo, openWorkoutIntroVideo } from '../../ui';
import { SVGIcon } from '../../ui/components';
import { defaultScale, isWeb } from '../../utils';
import { getGymTimezone } from '../../utils/gyms';
import { ScheduleWorkoutButton } from './ScheduleWorkoutButton';
import { StartWorkoutButton } from './StartWorkoutButton';

export interface IBookingDashProps {
  userProfile: UserProfile;
  workoutSessionService: any;
  navigation: any;
  nextWorkout: NextWorkout | null;
  bookings: UserWorkoutSession[];
  t: any;
  hasAccessToAStudioGym: boolean;
  openWorkoutIntroVideo: () => void;
  closeWorkoutIntroVideo: () => void;
}

export class WelcomeTileComponent extends React.Component<IBookingDashProps, any> {
  public state = {
    isLoading: false,
    showVideo: false,
  };

  public openWorkoutIntroVideo = () => {
    this.setState({ showVideo: true });
    this.props.openWorkoutIntroVideo();
  }
  public closeWorkoutIntroVideo = () => {
    this.setState({ showVideo: false });
    this.props.closeWorkoutIntroVideo();
  }

  public findNextBooking = () => {
    const { bookings = [], nextWorkout } = this.props;
    const booking = bookings.find(({ uniqueReference }) =>
      nextWorkout && uniqueReference === nextWorkout.uniqueReference);
    return booking;
  }

  public isAWorkoutStartingInOneHour = () => {
    const booking = this.findNextBooking();
    const isWorkoutStartingInOneHour = booking && isBookingInOneHour(booking);
    return isWorkoutStartingInOneHour;
  }

  public startWorkoutButton = () => {
    const { t, userProfile, hasAccessToAStudioGym } = this.props;
    const booking = this.findNextBooking();
    const isWorkoutStartingInOneHour = this.isAWorkoutStartingInOneHour();

    if (Platform.OS === 'web') {
      return (
        <View style={[styles.largeHeadingContainer, { backgroundColor: Theme.PRIMARY }]}>
          <Text style={styles.largeHeading}>
            {this.props.t('bookings:joinHomeWorkoutFromApp')}
          </Text>
        </View>
      );
    }

    if (isWorkoutStartingInOneHour) {
      return (
        <WorkoutButtonWithCountdownAndActions
          booking={booking}
          workoutSession={booking}
          navigation={this.props.navigation}
          userProfile={this.props.userProfile}
          gymTimezone={getGymTimezone(booking)}
          workoutSessionService={this.props.workoutSessionService}
          roundEdges={true}
          isWorkoutStartingInOneHour={isWorkoutStartingInOneHour}
        />
      );
    }
    if (hasAccessToAStudioGym) {
      return <ScheduleWorkoutButton
        userProfile={userProfile}
        workoutSessionService={this.props.workoutSessionService}
        t={t}
        navigation={this.props.navigation}
        buttonTitleOverride={t('dashboardStudioFirstWorkoutCallToAction')}
      />;
    }

    return (
      <View>
        <StartWorkoutButton
          userProfile={this.props.userProfile}
          workoutSessionService={this.props.workoutSessionService}
          buttonTitle={t('tryFirstWorkout')}
          overrideButtonSize={ButtonSize.MEDIUM}
          buttonType={ButtonTypes.NORMAL}
          overrideButtonTextStyle={[styles.workoutButtonText, { color: Theme.WHITE }]}
          containerStyle={{ marginBottom: 10 }}
          navigation={this.props.navigation}
        />
        <Button
          title={t('watchWorkoutIntro')}
          size={ButtonSize.MEDIUM}
          isLoading={false}
          icon={<SVGIcon Icon={circlePlayIcon} style={styles.buttonPlayIcon} color={Theme.PRIMARY} />}
          onPress={this.openWorkoutIntroVideo}
          buttonType={ButtonTypes.OUTLINE}
          overrideFontStyles={[styles.workoutButtonText, { color: Theme.PRIMARY }]}
        />
      </View>
    );
  }

  public render() {
    const { hasAccessToAStudioGym } = this.props;
    const aWorkoutIsStartingSoon = this.isAWorkoutStartingInOneHour();
    const demoTextKey = aWorkoutIsStartingSoon ? 'firstWorkoutStarting' : 'createdADemoWorkoutForYou';
    const headerImage = hasAccessToAStudioGym ? studioWorkoutImage : homeWorkoutImage;
    return (
      <>
      <TrackingImageTile
        containerStyle={{ height: defaultScale(367), marginBottom: defaultScale(15) }}
        imageSource={headerImage}
        title={`Welcome, ${this.props.userProfile.firstname}!`}
        titleStyle={styles.welcomeTextHeader}
        useGradient
        gradientHeight={hasAccessToAStudioGym ? '90%' : '80%'}
        gradientOpacity={0.83}
        actions={
          <View style={{ justifyContent: 'center' }}>
            {!hasAccessToAStudioGym &&
              <Text style={styles.welcomeTextMessage}>
                {this.props.t(demoTextKey)}
              </Text>
            }
            <View style={hasAccessToAStudioGym && { marginTop: defaultScale(20) }}>
              {this.startWorkoutButton()}
            </View>
          </View>
        } />
        {this.state.showVideo &&
          (<View style={[StyleSheet.absoluteFill, styles.videoPlayArea]}>
            <FitbeatLiteVideoIntro
              closeVideo={this.closeWorkoutIntroVideo}
              isVideoVisible={this.state.showVideo}
            />
          </View>)
        }
      </>
    );
  }
}

export const styles = StyleSheet.create({
  welcomeTextHeader: {
    ...Theme.textStyle.heading,
    color: Theme.WHITE,
    alignSelf: 'center',
  },
  videoPlayArea: {
    flex: 1,
    alignSelf: 'center',
    zIndex: 100,
  },
  welcomeTextMessage: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.WHITE,
    paddingHorizontal: 30,
    paddingTop: 10,
    paddingBottom: 25,
    textAlign: 'center',
  },
  workoutButtonText: {
    ...Theme.textStyle.bodyCopyBold,
  },
  largeHeading: {
    flex: 1,
    color: Theme.WHITE,
    textAlign: 'center',
    fontSize: 18,
    fontFamily: Theme.Custom800,
  },
  largeHeadingContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    height: 45,
    borderRadius: 5,
    padding: 35,
  },
  buttonPlayIcon: {
    height: 22.5,
    width: 22.5,
    paddingHorizontal: 20,
    alignSelf: 'center',
  },
  workoutButtonContainer: {
    paddingHorizontal: defaultScale(12),
    width: '100%',
    position: 'absolute',
    bottom: defaultScale(9),
  },
  workoutButtonStyles: {
    shadowColor: Platform.OS === 'android' ? Theme.BLACK : 'rgba(0, 0, 0, 0.37)',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 1,
    shadowRadius: 10,
    borderRadius: defaultScale(2),
    elevation: Platform.OS === 'android' ? 20 : 0,
    ...isWeb() ? { boxShadow: '0px 1px 5px 0px rgba(0, 0, 0, 0.37)' } : {},
  },
});

const mapDispatchToProps = (dispatch) => {
  return {
    openWorkoutIntroVideo: () => dispatch(openWorkoutIntroVideo()),
    closeWorkoutIntroVideo: () => dispatch(closeWorkoutIntroVideo()),
  };
};

const mapStateToProps = (state: IAppStore) => {
  return {
    hasAccessToAStudioGym: hasAccessToAStudioGymSelector(state),
  };
};

export const WelcomeTile =  translate(['dashboard', 'bookings'], { wait: true })
(connect(mapStateToProps, mapDispatchToProps)(WelcomeTileComponent));
